import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Tool data                                                           */
/* ------------------------------------------------------------------ */

interface Tool {
  href: string
  title: string
  description: string
  category: "buyer" | "agent"
  icon: React.ReactNode
}

const TOOLS: Tool[] = [
  {
    href: "/tools/stamp-duty-calculator",
    title: "Stamp duty calculator",
    description:
      "Calculate SDLT on your UK property purchase. First-time buyer relief, additional property surcharge, and non-UK resident rates.",
    category: "buyer",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
      </svg>
    ),
  },
  {
    href: "/tools/mortgage-calculator",
    title: "Mortgage calculator",
    description:
      "Calculate monthly mortgage repayments, total interest, and loan-to-value ratio. Compare repayment and interest-only.",
    category: "buyer",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
      </svg>
    ),
  },
  {
    href: "/tools/rental-yield-calculator",
    title: "Rental yield calculator",
    description:
      "Calculate gross yield, net yield, and monthly cashflow for buy-to-let investment properties.",
    category: "buyer",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
      </svg>
    ),
  },
  {
    href: "/tools/postcode-report",
    title: "Postcode report",
    description:
      "Get a free neighbourhood report for any UK postcode: crime rates, sold prices, flood risk, amenities, and a green living score.",
    category: "buyer",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
      </svg>
    ),
  },
  {
    href: "/tools/compare",
    title: "Compare postcodes",
    description:
      "Compare two UK postcodes side by side: property prices, crime, green score, schools, parks, and more.",
    category: "buyer",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
      </svg>
    ),
  },
  {
    href: "/tools/instruction-calculator",
    title: "Instruction revenue calculator",
    description:
      "Calculate how much extra revenue video marketing could earn your agency from additional instructions.",
    category: "agent",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
      </svg>
    ),
  },
  {
    href: "/tools/commission-calculator",
    title: "Commission calculator",
    description:
      "Calculate your commission per sale, monthly revenue, and what a small fee increase could mean annually.",
    category: "agent",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.121 7.629A3 3 0 0 0 9.017 9.43c-.023.212-.002.425.028.636l.506 3.541a4.5 4.5 0 0 1-.43 2.65L9 16.5l1.539-.513a2.25 2.25 0 0 1 1.422 0l.655.218a2.25 2.25 0 0 0 1.718-.122L15 15.75M8.25 12H12m9 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
  },
  {
    href: "/tools/marketing-roi-calculator",
    title: "Marketing ROI calculator",
    description:
      "Compare cost-per-lead across your marketing channels: portals, video, social media, print, and more.",
    category: "agent",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
      </svg>
    ),
  },
  {
    href: "/tools/listing-cost-calculator",
    title: "Listing cost calculator",
    description:
      "Compare the cost of marketing a listing with a videographer versus QuiteRight. See your annual savings.",
    category: "agent",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0 0 12 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971Zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 0 1-2.031.352 5.989 5.989 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971Z" />
      </svg>
    ),
  },
]

/* ------------------------------------------------------------------ */
/* Tool card                                                           */
/* ------------------------------------------------------------------ */

function ToolCard({ tool }: { tool: Tool }) {
  return (
    <Link
      href={tool.href}
      className="group flex flex-col rounded-xl border border-stone bg-white p-6 transition-all hover:border-red/20 hover:shadow-md hover:shadow-red/5"
    >
      <div className="flex size-10 items-center justify-center rounded-lg bg-cream text-slate transition-colors group-hover:bg-red/10 group-hover:text-red">
        {tool.icon}
      </div>
      <h3 className="mt-4 font-display text-lg font-bold text-charcoal transition-colors group-hover:text-red">
        {tool.title}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate">
        {tool.description}
      </p>
      <p className="mt-4 flex items-center gap-1.5 text-sm font-medium text-red">
        Open calculator
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className="size-4 transition-transform group-hover:translate-x-1"
        >
          <path
            fillRule="evenodd"
            d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
            clipRule="evenodd"
          />
        </svg>
      </p>
    </Link>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function ToolsIndexPage({ meta }: { meta: SeoMeta }) {
  const buyerTools = TOOLS.filter((t) => t.category === "buyer")
  const agentTools = TOOLS.filter((t) => t.category === "agent")

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Property Tools & Calculators",
            description: meta.description,
            numberOfItems: TOOLS.length,
            itemListElement: TOOLS.map((tool, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: tool.title,
              description: tool.description,
              url: `https://quiteright.co.uk${tool.href}`,
            })),
          })}
        </script>
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Free tools
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                Property tools &amp; calculators
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Free online calculators for UK estate agents, landlords, and home
                buyers. From stamp duty and mortgage repayments to agency
                commission and marketing ROI.
              </p>
              <div className="mt-8 flex items-center gap-6">
                <div className="text-center">
                  <p className="font-display text-3xl font-bold text-charcoal">9</p>
                  <p className="mt-1 text-xs text-slate">free tools</p>
                </div>
                <div className="h-10 w-px bg-stone" />
                <div className="text-center">
                  <p className="font-display text-3xl font-bold text-charcoal">100%</p>
                  <p className="mt-1 text-xs text-slate">free, no sign-up</p>
                </div>
                <div className="h-10 w-px bg-stone" />
                <div className="text-center">
                  <p className="font-display text-3xl font-bold text-charcoal">UK</p>
                  <p className="mt-1 text-xs text-slate">market data</p>
                </div>
              </div>
            </div>
            <div className="relative hidden md:block">
              <div className="overflow-hidden rounded-2xl shadow-xl">
                <OptimizedImage
                  eager
                  src={cdnUrl("/property-royal-york-800w.webp")}
                  srcSet={imageSrcSet("/property-royal-york-800w.webp")}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="Georgian property on Royal York Crescent, Bristol"
                  className="h-full w-full object-cover"
                  width={800}
                  height={533}
                />
              </div>
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md">
                <p className="text-xs font-medium text-warm-grey">All calculators</p>
                <p className="font-display text-xl font-bold text-white">completely free</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Buyer & seller tools */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              For buyers, sellers &amp; landlords
              <span className="text-red">.</span>
            </h2>
            <p className="mt-2 text-sm text-slate">
              Essential calculators for anyone buying, selling, or letting
              property in the UK.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {buyerTools.map((tool) => (
              <ToolCard key={tool.href} tool={tool} />
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Visual break */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <div className="grid items-center gap-8 md:grid-cols-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                  Built for UK property
                </p>
                <h2 className="mt-4 font-display text-2xl font-bold text-charcoal md:text-3xl">
                  Numbers you can trust
                  <span className="text-red">.</span>
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-slate">
                  Every calculator uses current UK tax rates, average market
                  data, and industry-standard formulas. Whether you&rsquo;re an
                  agent calculating commission or a buyer working out stamp
                  duty, the numbers are accurate and up to date.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="rounded-lg border border-stone bg-cream p-4">
                    <p className="font-display text-xl font-bold text-charcoal">&pound;0</p>
                    <p className="mt-1 text-xs text-slate">Always free, no hidden fees</p>
                  </div>
                  <div className="rounded-lg border border-stone bg-cream p-4">
                    <p className="font-display text-xl font-bold text-charcoal">2026</p>
                    <p className="mt-1 text-xs text-slate">Current UK tax rates</p>
                  </div>
                </div>
              </div>
              <div className="overflow-hidden rounded-2xl shadow-lg">
                <OptimizedImage
                  src={cdnUrl("/property-canynge-square-800w.webp")}
                  srcSet={imageSrcSet("/property-canynge-square-800w.webp")}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="Modern property interior at Canynge Square, Bristol"
                  className="h-full w-full object-cover"
                  width={800}
                  height={533}
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Agent tools */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              For estate agents
              <span className="text-red">.</span>
            </h2>
            <p className="mt-2 text-sm text-slate">
              Professional tools to help you grow your agency, optimise your
              marketing, and justify your fees.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {agentTools.map((tool) => (
              <ToolCard key={tool.href} tool={tool} />
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* More resources */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              More resources
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal stagger className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              {
                href: "/guides",
                title: "Guides",
                desc: "Free guides for estate agents and home sellers.",
              },
              {
                href: "/glossary",
                title: "Property glossary",
                desc: "Property marketing terms explained.",
              },
              {
                href: "/directory",
                title: "Agent directory",
                desc: "Browse and compare local estate agents.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            The tool that pays for itself
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            QuiteRight turns your listings into scroll-stopping video ads in
            under 60 seconds. More viewings, faster sales, and vendors who
            remember your name.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
